import React from "react";
import ApproverTracker from "./ApproverTracker";

const employeeNumber = (value: unknown) => {
  const text = String(value ?? "").trim();
  return /^\d+$/.test(text) ? text.padStart(8, "0") : text;
};

const roleLabel = (role: string) => ({
  EMP1_CONSENT: "Applicant Consent",
  EMP2_CONSENT: "Second Party Consent",
  CO_EMP1: "Applicant Controlling Officer",
  CO_EMP2: "Second Party Controlling Officer",
}[role] || role);

export const controllingOfficerLabel = (record: any, employee: any, index: number) => {
  const suffix = index + 1;
  const officer = employee?.controllingOfficer;
  const legacy = employee?.controllingOfficerEmp || record[`controllingOfficerEmp${suffix}`];
  const name = employee?.controllingOfficerName || record[`controllingOfficerName${suffix}`] ||
    (typeof officer === "object" ? officer?.name : officer) ||
    (legacy && !/^\d+$/.test(String(legacy).trim()) ? legacy : "");
  const id = employee?.controllingOfficerEmpId || employee?.controllingOfficerEmpNo ||
    record[`controllingOfficerEmpId${suffix}`] || record[`controllingOfficerEmpNo${suffix}`] ||
    (typeof officer === "object" ? officer?.empId || officer?.empNo : "") ||
    (/^\d+$/.test(String(legacy ?? "").trim()) ? legacy : "");
  const approval = (record.approvalDetails || []).find((step: any) =>
    id && employeeNumber(step.approverId) === employeeNumber(id));
  return `${name || approval?.approverName || "Name unavailable"} (Emp No: ${employeeNumber(id) || "Unavailable"})`;
};

const MutualExchangeHistory: React.FC<{ record: any }> = ({ record }) => {
  const source = [record.approvalDetails, record.approvers, record.approverDetails, record.approversConfig, record.approvals]
    .find(value => Array.isArray(value) && value.length) || [];
  const steps = source.map((step: any, index: number) => ({
    id: step.id,
    empId: employeeNumber(step.approverId || step.empId || step.employeeNumber),
    name: step.approverName || step.name || step.employeeName || "Name unavailable",
    status: String(step.status || step.approverStatus || "PENDING").toUpperCase(),
    level: Number(step.approvalLevel || step.stage || index + 1),
    role: roleLabel(step.approvedByRole || step.role || step.approverRole || ""),
    image: step.image || step.avatar,
    assigned: [true, "true", 1, "1"].includes(step.isAssigned),
    remarks: step.remarks || step.approverRemarks,
    date: step.approvedAt || step.updatedAt,
  })).sort((a: any, b: any) => a.level - b.level);
  const isPending = String(record.status).toUpperCase() === "PENDING";
  const current = record.activeApproval || record.currentApprover;
  const currentStep = current && steps.find((step: any) =>
    current.id != null ? String(step.id) === String(current.id) :
      step.empId === employeeNumber(current.approverId || current.empId || current.employeeNumber) &&
      (current.stage == null || step.level === Number(current.stage)));
  const pending = isPending ? (current && typeof current === "object" ? {
    name: current.name || current.approverName || currentStep?.name || "Name unavailable",
    empId: employeeNumber(current.empId || current.approverId || current.employeeNumber),
    role: roleLabel(current.approvedByRole || current.role || current.approverRole || currentStep?.role || ""),
  } : steps.find((step: any) => step.assigned && step.status === "PENDING") ||
  steps.find((step: any) => step.status === "PENDING")) : null;
  const tracker: React.ComponentProps<typeof ApproverTracker>["approvers"] = [
    { id: "submitted", name: `${record.emp1?.name || "Applicant"}${record.emp1?.empNo ? ` (${employeeNumber(record.emp1.empNo)})` : ""}`, status: "Submitted", stepName: "Request Submitted" },
    ...steps.map((step: any, index: number) => ({
      id: `${step.empId}-${index}`,
      empId: step.empId,
      image: step.image,
      name: `${step.name} (Emp No: ${step.empId || "Unavailable"})`,
      status: (step.status === "APPROVED" ? "Approved" : step.status === "REJECTED" ? "Rejected" : step.status === "CONFIRMED" ? "Confirmed" : "Pending") as "Approved" | "Rejected" | "Confirmed" | "Pending",
      stepName: step.role || `L${step.level} Approval`,
    })),
  ];

  return <section className="border rounded-3 p-3 mb-3" aria-label="Event History">
    <h6 className="fw-bold mb-3">Event History</h6>
    {record.approvalProgress != null && <div className="small text-muted mb-2">
      Approval progress: {record.approvalProgress}
      {record.currentApprovalStage != null && ` | Current stage: ${record.currentApprovalStage}`}
    </div>}
    {isPending && <div className="alert alert-warning py-2 mb-3">
      <strong>Pending with: </strong>{pending ? `${pending.name} (Emp No: ${pending.empId || "Unavailable"})${pending.role ? ` · ${pending.role}` : ""}` : "Approver details unavailable"}
    </div>}
    <ApproverTracker approvers={tracker} showScrollbar />
    {!steps.length && <div className="text-muted small mt-2">Approval flow details are not available for this request.</div>}
    {steps.filter((step: any) => step.remarks).map((step: any, index: number) => <div className="bg-light rounded p-2 mt-2" key={index}>
      <div className="small fw-bold">{step.name} (Emp No: {step.empId || "Unavailable"}) · {step.status}</div>
      {step.date && <div className="small text-muted">{Number.isNaN(new Date(step.date).getTime()) ? step.date : new Date(step.date).toLocaleString()}</div>}
      <div style={{ whiteSpace: "pre-wrap" }}>{step.remarks}</div>
    </div>)}
  </section>;
};

export default MutualExchangeHistory;
